import React from "react";
import { clearStorage } from ".";
import { ErrorBoundaryContainer } from "../ErrorBoundary";
import { Button } from "@/components/ui/button";

type UserType = "doctor" | "patient";

interface ErrorFallbackUIProps {
  error: Error;
  isEmergency?: boolean;
}

const resolveUserTypeFromPath = (path: string): UserType => {
  if (path.includes("/doctor/")) {
    return "doctor";
  }
  return "patient";
};

const getConsultationIdFromPath = (path: string): string | null => {
  const doctorWaitingMatch = path.match(/\/doctor\/([^/]+)$/);
  if (doctorWaitingMatch && doctorWaitingMatch[1]) {
    return doctorWaitingMatch[1];
  }

  const chatMatch = path.match(/\/chat\/([^/]+)$/);
  if (chatMatch && chatMatch[1]) {
    return chatMatch[1].split("_")[0];
  }

  const videoMatch = path.match(/\/video\/([^/]+)$/);
  if (videoMatch && videoMatch[1]) {
    return videoMatch[1];
  }

  const waitingMatch = path.match(/\/([^/]+)$/);
  if (waitingMatch && waitingMatch[1] && !path.includes("/doctor/")) {
    return waitingMatch[1];
  }

  return null;
};

export function ErrorFallbackUI({
  error,
  isEmergency = false,
}: ErrorFallbackUIProps) {
  const currentPath = window?.location?.pathname || "";
  const consultationId = getConsultationIdFromPath(currentPath);

  const header = isEmergency
    ? "Critical Error - App Failed to Load"
    : "Something Went Wrong";

  return (
    <ErrorBoundaryContainer header={header}>
      <>
        {isEmergency && (
          <div className="mb-4 p-3 bg-warning-light border border-warning rounded-lg">
            <p className="type-caption text-warning-foreground text-center">
              A critical error occurred before the app could fully initialize.
            </p>
          </div>
        )}

        <div className="flex justify-center w-full">
          <Button
            name="refresh-page"
            aria-label="Refresh page"
            data-action="refresh-page"
            onClick={() => {
              clearStorage();
              const prefix =
                resolveUserTypeFromPath(currentPath) === "doctor"
                  ? "/doctor"
                  : "";
              window.location.href = `${prefix}/${consultationId}`;
            }}
          >
            Refresh
          </Button>
        </div>

        {process.env.NODE_ENV === "development" && error && (
          <details className="mt-6 p-4 bg-background rounded-lg">
            <summary className="cursor-pointer font-medium text-foreground mb-2">
              Error Details (Development Only)
            </summary>
            <pre className="text-xs text-foreground-muted overflow-auto">
              {error.toString()}
              {"\n\n"}
              {error.stack}
            </pre>
          </details>
        )}
      </>
    </ErrorBoundaryContainer>
  );
}
